import ImageRenderer from "@components/common/Image";
import { HeroBannerPropsModel } from "@models/hero-banner";
import { StrapiGatsbyImageType } from "@models/strapi-components/common";
import { AuthorResponseModel } from "@models/users";
import { motion } from "framer-motion";
import { HeadFC, PageProps, graphql } from "gatsby";
import React from "react";
import TextOnlyHeroBanner from "@components/common/HeroBanner/TextOnlyHero";
import Layout from "@components/common/Layout";
import { SeoDataModel } from "@models/seo";
import { PageContextModel } from "@models/page";
import SEO from "@components/common/Layout/SEO";
import dayjs from "dayjs";
import { RelatedBlogsModel } from "@models/blog";
import RelatedBlogs from "@components/Blogs/RelatedBlogs";

export interface BlogDataModel {
  strapiBlog: {
    title: string;
    id: string;
    date: string;
    seo: SeoDataModel;
    content: {
      data: { childMarkdownRemark: { html: string } };
    };
    topBanner: Omit<HeroBannerPropsModel, "topbanner"> & {
      blogImage: StrapiGatsbyImageType;
    };
    author: AuthorResponseModel[];
    relatedBlogs: RelatedBlogsModel;
  };
}

const Blog = (props: PageProps<BlogDataModel, PageContextModel>) => {
  const { id, content, topBanner, author, date, relatedBlogs } =
    props?.data?.strapiBlog ?? {};

  const topBannerWithDate = {
    ...topBanner,
    title: dayjs(date)?.format("DD MMM YYYY") ?? "",
  };

  return (
    <Layout>
      <TextOnlyHeroBanner {...topBannerWithDate} path="" author={author} />
      <div className="flex flex-col items-center py-12 md:py-[60px] xl:py-20">
        <div>
          <ImageRenderer image={topBanner?.blogImage} alt={id} />
        </div>
        <div className="px-4 md:px-24 xl:px-[250px] py-20">
          <motion.div
            dangerouslySetInnerHTML={{
              __html: content?.data?.childMarkdownRemark?.html ?? "<></>",
            }}
            className="prose"
          />
        </div>
        <RelatedBlogs relatedBlogs={relatedBlogs} />
      </div>
    </Layout>
  );
};
export default Blog;

export const query = graphql`
  query BlogPage($slug: String) {
    strapiBlog(slug: { eq: $slug }) {
      id
      date
      title
      content {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      topBanner {
        bigHeader
        ctaBtnLink
        ctaBtnText
        title
        id
        blogImage: image {
          localFile {
            childImageSharp {
              gatsbyImageData(
                height: 667
                width: 1117
                quality: 100
                placeholder: NONE
              )
            }
          }
        }
      }
      author {
        ...heroBannerAuthor
      }
      seo {
        ...seoFragment
      }
      relatedBlogs {
        title
        blogs {
          title
          subTitle
          slug
          id
          topBanner {
            image {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 307, quality: 100)
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const Head: HeadFC<BlogDataModel, PageContextModel> = ({
  data,
  location,
}) => {
  const { title, seo } = data?.strapiBlog ?? {};
  const pathname = location?.pathname;
  return <SEO title={title} pathname={pathname} {...(seo ?? {})} />;
};
